import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import AuthContext from "@/contexts/AuthContext";
import { ClubsService } from "@/core/http/services/ClubsService";
import { DashboardService } from "@/core/http/services/DashboardService";
import SummaryCard from "@/pages/private/Admin/dashboard/components/SummaryCard";
import ApeakClub from "@/images/apeak_club.png";

const INVITE_LOGIN_BASE_URL = "https://apeakclubmc.codx.dev.br/login";

const copyTextToClipboard = async (text) => {
  if (!text) return false;

  // Prefer async clipboard API when available (requires secure context).
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fallback below
  }

  // Fallback for HTTP / non-secure contexts.
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-1000px";
    textarea.style.left = "-1000px";
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

const formatCreatedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const SuperAdminHub = () => {
  const navigate = useNavigate();
  const { userLoggedData, setSelectedClubId, updateAuthPatch } =
    useContext(AuthContext);

  const [clubsState, setClubsState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [globalStats, setGlobalStats] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  const clubs = useMemo(() => {
    if (clubsState.length > 0) return clubsState;
    return Array.isArray(userLoggedData?.clubs) ? userLoggedData.clubs : [];
  }, [clubsState, userLoggedData?.clubs]);

  const filteredClubs = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return clubs;
    return clubs.filter((club) =>
      String(club?.name || "")
        .toLowerCase()
        .includes(query),
    );
  }, [clubs, search]);

  const role = userLoggedData?.role?.toUpperCase?.() || "";

  const fetchClubs = useCallback(async () => {
    if (role !== "SUPER_ADMIN") return;

    setLoading(true);
    setError(null);
    const { status, data, message } = await ClubsService.getAll();
    if (status !== 200) {
      setError(message || "Erro ao buscar clubes.");
      setLoading(false);
      return;
    }
    setClubsState(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [role]);

  const fetchGlobalDashboard = useCallback(async () => {
    if (role !== "SUPER_ADMIN") return;
    setGlobalLoading(true);
    setGlobalError(null);
    const { status, data, message } = await DashboardService.getGlobalAdminDashboard();
    if (status !== 200) {
      setGlobalError(message || "Erro ao buscar dashboard global.");
      setGlobalLoading(false);
      return;
    }
    setGlobalStats(data?.stats ?? null);
    setGlobalLoading(false);
  }, [role]);

  const handleSelect = async (clubId) => {
    if (clubsState.length > 0) {
      await updateAuthPatch({ clubs: clubsState });
    }
    await setSelectedClubId(clubId);
    navigate(`/c/${clubId}/admin/dashboard`);
  };

  const handleCopyInviteToken = useCallback(async (inviteToken) => {
    if (!inviteToken) return;
    try {
      const inviteLink = `${INVITE_LOGIN_BASE_URL}/${inviteToken}`;
      const ok = await copyTextToClipboard(inviteLink);
      if (!ok) {
        setSnackbar({
          open: true,
          message: "Não foi possível copiar automaticamente. Copie manualmente o invite token.",
        });
        return;
      }
      setSnackbar({ open: true, message: "Link de convite copiado." });
    } catch {
      setSnackbar({ open: true, message: "Não foi possível copiar o link." });
    }
  }, []);

  useEffect(() => {
    if (!userLoggedData) return;
    if (role !== "SUPER_ADMIN") {
      navigate("/user/redirect");
    }
  }, [navigate, role, userLoggedData]);

  useEffect(() => {
    fetchClubs();
  }, [fetchClubs]);

  useEffect(() => {
    fetchGlobalDashboard();
  }, [fetchGlobalDashboard]);

  if (!userLoggedData) return null;
  if (role !== "SUPER_ADMIN") return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        bgcolor: "background.default",
        backgroundImage:
          "radial-gradient(circle at 20% 20%, rgba(25,118,210,0.18), transparent 45%), radial-gradient(circle at 80% 30%, rgba(156,39,176,0.12), transparent 45%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          opacity: 0.06,
          transform: "scale(1.15)",
          filter: "grayscale(1)",
        }}
      >
        <Box
          component="img"
          src={ApeakClub}
          alt=""
          sx={{
            width: { xs: 420, sm: 620, md: 760 },
            maxWidth: "90vw",
            objectFit: "contain",
          }}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 980,
          p: { xs: 2.25, sm: 3 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: "0 18px 50px rgba(0,0,0,0.25)",
          position: "relative",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ justifyContent: "center" }}
          >
            <Box
              component="img"
              src={ApeakClub}
              alt="Apeak"
              sx={{
                height: { xs: 42, sm: 48 },
                width: "auto",
                objectFit: "contain",
                opacity: 0.92,
                flexShrink: 0,
              }}
            />
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.05 }}>
                HUB - Super Admin
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Selecione um clube para entrar na área administrativa.
              </Typography>
            </Box>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              p: 2,
              bgcolor: "background.default",
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Visão geral da plataforma
              </Typography>

              {globalLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Carregando dashboard...
                </Typography>
              ) : globalError ? (
                <Typography variant="body2" color="error">
                  {globalError}
                </Typography>
              ) : globalStats ? (
                <Box sx={{ overflow: "hidden" }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                    {[
                      { id: "clubs", value: globalStats.totalClubs ?? "-", label: "Clubes", subLabel: "Cadastrados" },
                      { id: "players", value: globalStats.totalPlayers ?? "-", label: "Jogadores", subLabel: "Cadastrados" },
                      { id: "rankings", value: globalStats.totalRankings ?? "-", label: "Rankings", subLabel: "Total" },
                    ].map((item) => (
                      <Box key={item.id} sx={{ flex: "1 1 240px", minWidth: 220 }}>
                        <SummaryCard item={item} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhum dado disponível.
                </Typography>
              )}
            </Stack>
          </Paper>

          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar clube..."
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Carregando clubes...
            </Typography>
          ) : error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : clubs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum clube disponível.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.25,
              }}
            >
              {filteredClubs.map((club) => {
                const createdAt = formatCreatedAt(club?.createdAt);

                return (
                  <Paper
                    key={club?.id || club?.name}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.default",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 900, lineHeight: 1.15 }}
                          noWrap
                          title={club?.name || "Clube"}
                        >
                          {club?.name || "Clube"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 700 }}
                        >
                          {createdAt ? `Criado em ${createdAt}` : "—"}
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        onClick={() => handleSelect(club?.id)}
                        disabled={!club?.id}
                        sx={{
                          textTransform: "none",
                          fontWeight: 900,
                          borderRadius: 2,
                          px: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Administrar
                      </Button>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      {club?.inviteToken ? (
                        <>
                          <Chip
                            size="small"
                            label={club.inviteToken}
                            sx={{
                              maxWidth: "100%",
                              fontWeight: 800,
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                            }}
                          />
                          <IconButton
                            aria-label="Copiar invite token"
                            size="small"
                            onClick={() =>
                              handleCopyInviteToken(club.inviteToken)
                            }
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Sem invite token
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2200}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default SuperAdminHub;
